import React, { useState, useEffect, useRef } from 'react';
import { API_ENDPOINTS } from '../config/apiRouting';
import { getUserRole, hasPermission } from '../utils/roleUtils';
import { useAuth } from '../contexts/AuthContext';
import './MenuItemManagement.css'; // Reuse existing styles
import './SecurityAuditManagement.css'; // Security audit specific styles

interface SecurityAuditManagementProps {
  user: any;
}

interface SecurityAudit {
  auditId: string;
  storeId: string;
  enterpriseId: string;
  auditType: 'PCI_LEVEL_1' | 'SOC2_TYPE_II';
  status: 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'EXPIRED';
  createdAt: string;
  updatedAt: string;
  expirationDate: string;
  overallScore: number;
  requirements: Record<string, AuditRequirement>;
  automatedChecks: Record<string, any>;
  createdBy: string;
}

interface AuditRequirement {
  description: string;
  status: 'COMPLETED' | 'IN_PROGRESS' | 'PENDING' | 'NON_COMPLIANT';
  lastChecked?: string;
  notes: string;
  evidence: string[];
  remediation?: string;
  documents?: AuditDocument[];
  requiredDocuments?: string[];
  uploadedDocuments?: Record<string, UploadedDocumentInfo>;
}

interface UploadedDocumentInfo {
  fileName: string;
  uploadedAt: string;
  uploadedBy: string;
  fileSize: number;
  documentId: string;
  autoGenerated?: boolean;
  pdfContent?: string; // Base64-encoded PDF
}

interface AuditDocument {
  documentId: string;
  fileName: string;
  fileSize: number;
  uploadedAt: string;
  uploadedBy: string;
  s3Key?: string;
  requirementId?: string;
  documentType: string;
}

interface AuditEvent {
  timestamp: string;
  eventType: string;
  description: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  userId?: string;
  ipAddress?: string;
}

interface ComplianceCheck {
  checkId: string;
  storeId: string;
  checkType: string;
  results: Record<string, any>;
  lastChecked: string;
  overallCompliance: number;
}

interface Store {
  storeId: string;
  name: string;
  enterpriseId: string;
  address: string;
}

// Documents that can be auto-generated from AWS infrastructure
const AUTO_GENERATABLE_DOCUMENTS = [
  'Network Diagram',
  'Firewall Configuration Standards',
  'Firewall Ruleset Documentation',
  'Access Control Matrix',
  'Access Control Lists',
  'Authentication Policy',
  'Password Policy',
  'MFA Implementation Policy',
  'MFA Configuration Guide',
  'User Provisioning Procedures',
  'Database Encryption Configuration',
  'Storage Encryption Policy',
  'TLS/SSL Certificate Documentation',
  'Network Encryption Policy',
  'Encryption Implementation Guide',
  'Data Encryption Policy',
  'Key Management Procedures',
  'Logging and Monitoring Policy',
  'Audit Trail Configuration',
  'Security Monitoring Policy',
  'Sample Security Event Logs',
  'Sample Audit Logs',
  'Performance Monitoring Procedures',
  'System Health Reports'
];

const SecurityAuditManagement: React.FC<SecurityAuditManagementProps> = ({ user }) => {
  const [audits, setAudits] = useState<SecurityAudit[]>([]);
  const [stores, setStores] = useState<Store[]>([]);
  const [enterprises, setEnterprises] = useState<any[]>([]);
  const [selectedStore, setSelectedStore] = useState<string>('');
  const [selectedEnterprise, setSelectedEnterprise] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'audits' | 'requirements' | 'events' | 'checks' | 'documents'>('overview');
  const [selectedAudit, setSelectedAudit] = useState<SecurityAudit | null>(null);
  const [auditEvents, setAuditEvents] = useState<AuditEvent[]>([]);
  const [complianceChecks, setComplianceChecks] = useState<ComplianceCheck[]>([]);
  const [runningChecks, setRunningChecks] = useState(false);

  // Document upload state
  const [uploadingDocument, setUploadingDocument] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedRequirementForUpload, setSelectedRequirementForUpload] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [allDocuments, setAllDocuments] = useState<AuditDocument[]>([]);
  const [generatingDocument, setGeneratingDocument] = useState<string | null>(null); // Document name being generated

  const { user: currentUser, getIdToken } = useAuth();
  const userRole = getUserRole(currentUser);

  // Permission checks
  const canCreateAudits = hasPermission(currentUser, 'security_audit_create');
  const canViewAudits = hasPermission(currentUser, 'security_audit_view');
  const canRunChecks = hasPermission(currentUser, 'security_audit_automated_checks');
  

  useEffect(() => {
    fetchEnterprises();
  }, []);

  useEffect(() => {
    if (selectedEnterprise) {
      fetchStores();
    }
  }, [selectedEnterprise]);

  useEffect(() => {
    if (selectedStore) {
      fetchAudits();
    }
  }, [selectedStore]);

  useEffect(() => {
    if (selectedAudit) {
      fetchAllDocuments();
    }
  }, [selectedAudit]);

  const fetchEnterprises = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const url = API_ENDPOINTS.enterprises();
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${await getIdToken()}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch enterprises: ${response.status}`);
      }
      
      const data = await response.json();

      // Handle both array response and {data: []} wrapped response
      const enterprisesArray = Array.isArray(data) ? data : (data.data || data.enterprises || []);
      setEnterprises(enterprisesArray);
      
      // Auto-select first enterprise if user has limited access
      if (enterprisesArray && enterprisesArray.length === 1) {
        setSelectedEnterprise(enterprisesArray[0].enterpriseId);
      }
    } catch (error) {
      setError(`Failed to load enterprises: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchStores = async () => {
    if (!selectedEnterprise) return;

    try {
      setLoading(true);
      setError(null);
      
      const url = API_ENDPOINTS.enterpriseStores(selectedEnterprise);
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${await getIdToken()}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch stores: ${response.status}`);
      }
      
      const data = await response.json();

      // Handle both array response and {data: []} wrapped response
      const storesArray = Array.isArray(data) ? data : (data.data || data.stores || []);
      setStores(storesArray);
    } catch (error) {
      setError(`Failed to load stores: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchAudits = async () => {
    if (!selectedStore || !canViewAudits) return;

    try {
      setLoading(true);
      const response = await fetch(API_ENDPOINTS.securityAudits(selectedStore), {
        headers: {
          'Authorization': `Bearer ${await getIdToken()}`,
        },
      });
      
      if (!response.ok) {
        if (response.status === 403) {
          setError('Security audit API access is currently restricted. Please contact your administrator to configure Lambda function permissions.');
        } else {
          throw new Error(`Failed to fetch audits: ${response.status}`);
        }
        return;
      }
      
      const data = await response.json();
      setAudits(data.audits || data.data || []);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      if (errorMessage.includes('Forbidden') || errorMessage.includes('403')) {
        setError('Security audit API is not accessible. Lambda function URL may need permission configuration.');
      } else {
        setError('Failed to load security audits. Please try again later.');
      }
    } finally {
      setLoading(false);
    }
  };

  const createNewAudit = async (auditType: 'PCI_LEVEL_1' | 'SOC2_TYPE_II') => {
    if (!selectedStore || !canCreateAudits) return;

    try {
      setLoading(true);
      const response = await fetch(API_ENDPOINTS.securityAudits(selectedStore), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await getIdToken()}`,
        },
        body: JSON.stringify({
          auditType,
          enterpriseId: selectedEnterprise,
          createdBy: currentUser?.getUsername() || 'system'
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to create audit: ${response.status}`);
      }

      const data = await response.json();
      setAudits(prev => [data.audit || data.data, ...prev]);
      setError(null);
    } catch (error) {
      setError('Failed to create security audit');
    } finally {
      setLoading(false);
    }
  };

  const runAutomatedChecks = async () => {
    if (!selectedStore || !canRunChecks) return;

    try {
      setRunningChecks(true);
      const response = await fetch(API_ENDPOINTS.securityAutomatedChecks(selectedStore), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await getIdToken()}`,
        },
        body: JSON.stringify({
          checkTypes: ['passwordPolicy', 'keyRotation', 'backupStatus', 'encryptionStatus', 'networkSecurity']
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to run checks: ${response.status}`);
      }

      const data = await response.json();
      setComplianceChecks(prev => [data.data, ...prev]);
      setError(null);
    } catch (error) {
      setError('Failed to run automated compliance checks');
    } finally {
      setRunningChecks(false);
    }
  };

  const runAdHocAudit = async (auditType: 'PCI_LEVEL_1' | 'SOC2_TYPE_II') => {
    if (!selectedStore || !canCreateAudits) return;

    try {
      setLoading(true);
      const response = await fetch(`${API_ENDPOINTS.securityAudits(selectedStore)}/run-now`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await getIdToken()}`,
        },
        body: JSON.stringify({
          auditType,
          enterpriseId: selectedEnterprise,
          runType: 'AD_HOC',
          createdBy: currentUser?.getUsername() || 'system'
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to run ad-hoc audit: ${response.status}`);
      }

      const data = await response.json();
      setAudits(prev => [data.audit || data.data, ...prev]);
      setError(null);

      // Auto-select the new audit for viewing
      setSelectedAudit(data.audit || data.data);
      setActiveTab('requirements');

    } catch (error) {
      setError(`Failed to run ad-hoc ${auditType.replace('_', ' ')} audit: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  // Document upload for requirements
  const uploadDocumentForRequirement = async (requirementId: string, documentName: string, file: File) => {
    if (!selectedAudit) return;

    try {
      setUploadingDocument(true);

      const documentId = `doc_${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;

      // Update the requirement with the new document
      const updatedRequirements = { ...selectedAudit.requirements };

      // Initialize uploadedDocuments if it doesn't exist
      if (!updatedRequirements[requirementId].uploadedDocuments) {
        updatedRequirements[requirementId].uploadedDocuments = {};
      }

      // Store the document info mapped to the required document name
      updatedRequirements[requirementId].uploadedDocuments![documentName] = {
        fileName: file.name,
        uploadedAt: new Date().toISOString(),
        uploadedBy: currentUser?.getUsername() || 'system',
        fileSize: file.size,
        documentId
      };

      // Update audit in backend
      const response = await fetch(API_ENDPOINTS.securityAudit(selectedAudit.auditId), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await getIdToken()}`,
        },
        body: JSON.stringify({
          requirements: updatedRequirements
        })
      });

      if (!response.ok) {
        throw new Error('Failed to upload document');
      }

      const data = await response.json();
      const updatedAudit = data.audit || data.data;
      setSelectedAudit(updatedAudit);

      // Update audits list
      setAudits(prev => prev.map(a =>
        a.auditId === selectedAudit.auditId ? updatedAudit : a
      ));

      // Update all documents list
      fetchAllDocuments(updatedAudit);
      setError(null);

    } catch (error) {
      setError('Failed to upload document');
      console.error('Document upload error:', error);
    } finally {
      setUploadingDocument(false);
      setSelectedRequirementForUpload(null);
    }
  };

  const fetchAllDocuments = (audit?: SecurityAudit) => {
    const auditToUse = audit || selectedAudit;
    if (!auditToUse) return;

    const docs: AuditDocument[] = [];
    Object.entries(auditToUse.requirements).forEach(([reqId, req]) => {
      // Get documents from uploadedDocuments structure
      if (req.uploadedDocuments) {
        Object.entries(req.uploadedDocuments).forEach(([docName, docInfo]) => {
          docs.push({
            documentId: docInfo.documentId,
            fileName: docInfo.fileName,
            fileSize: docInfo.fileSize,
            uploadedAt: docInfo.uploadedAt,
            uploadedBy: docInfo.uploadedBy,
            requirementId: reqId,
            documentType: docName // Store the required document name as type
          });
        });
      }

      // Also get legacy documents if any exist
      if (req.documents && req.documents.length > 0) {
        docs.push(...req.documents);
      }
    });

    // Sort by upload date, most recent first
    docs.sort((a, b) => new Date(b.uploadedAt).getTime() - new Date(a.uploadedAt).getTime());
    setAllDocuments(docs);
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>, requirementId: string, documentName: string) => {
    if (e.target.files && e.target.files[0]) {
      await uploadDocumentForRequirement(requirementId, documentName, e.target.files[0]);
    }
  };

  const downloadGeneratedDocument = (documentInfo: UploadedDocumentInfo, documentName: string) => {
    if (!documentInfo.pdfContent) {
      console.error('No PDF content available for download');
      return;
    }

    try {
      // Convert base64 to blob
      const byteCharacters = atob(documentInfo.pdfContent);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = documentInfo.fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading document:', error);
      setError('Failed to download document');
    }
  };

  const autoGenerateDocument = async (requirementId: string, documentName: string) => {
    if (!selectedAudit) return;

    try {
      setGeneratingDocument(documentName);

      // Get username (often the email for Cognito users)
      const userEmail = currentUser?.getUsername() || 'system';

      const response = await fetch(
        API_ENDPOINTS.securityAudit(`${selectedAudit.auditId}/requirements/${requirementId}/generate-document`),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${await getIdToken()}`,
          },
          body: JSON.stringify({
            documentName,
            username: userEmail
          })
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to generate document');
      }

      const data = await response.json();

      // Refresh the audit to get the updated document
      const auditResponse = await fetch(API_ENDPOINTS.securityAudit(selectedAudit.auditId), {
        headers: {
          'Authorization': `Bearer ${await getIdToken()}`,
        }
      });

      if (auditResponse.ok) {
        const auditData = await auditResponse.json();
        const updatedAudit = auditData.audit || auditData.data;
        setSelectedAudit(updatedAudit);

        // Update audits list
        setAudits(prev => prev.map(a =>
          a.auditId === selectedAudit.auditId ? updatedAudit : a
        ));

        // Update all documents list
        fetchAllDocuments(updatedAudit);
      }

      setError(null);

    } catch (error: any) {
      setError(`Failed to auto-generate document: ${error.message}`);
      console.error('Document generation error:', error);
    } finally {
      setGeneratingDocument(null);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'COMPLETED': return 'green';
      case 'IN_PROGRESS': return 'blue';
      case 'PENDING': return 'orange';
      case 'EXPIRED': return 'red';
      default: return 'gray';
    }
  };

  const getComplianceColor = (score: number) => {
    if (score >= 90) return 'green';
    if (score >= 70) return 'orange';
    return 'red';
  };

  return (
    <div className="menu-item-management">
      <div className="management-header">
        <h2>Security Auditing & Compliance</h2>
        <p>PCI DSS Level 1 and SOC 2 Type II compliance tracking and management system</p>
        <div className="compliance-disclaimer" style={{
          background: '#fff3cd',
          border: '1px solid #ffc107',
          borderRadius: '4px',
          padding: '12px',
          marginTop: '12px',
          fontSize: '0.9rem'
        }}>
          <strong>⚠️ Important:</strong> This system is for internal compliance tracking and preparation.
          Formal PCI DSS Level 1 certification requires an annual on-site QSA (Qualified Security Assessor) audit.
          SOC 2 Type II certification requires a 3-12 month independent CPA firm audit.
          Use this tool to prepare for professional audits and maintain ongoing compliance documentation.
        </div>
      </div>

      {error && (
        <div className="error-message">
          <p>{error}</p>
          <button onClick={() => setError(null)}>Dismiss</button>
        </div>
      )}

      {/* Enterprise and Store Selection */}
      <div className="filters-section">
        <div className="filter-group">
          <label>Enterprise:</label>
          <select
            value={selectedEnterprise}
            onChange={(e) => setSelectedEnterprise(e.target.value)}
            disabled={loading}
          >
            <option value="">Select Enterprise</option>
            {enterprises.map((enterprise) => (
              <option key={enterprise.enterpriseId} value={enterprise.enterpriseId}>
                {enterprise.name}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label>Store:</label>
          <select
            value={selectedStore}
            onChange={(e) => setSelectedStore(e.target.value)}
            disabled={loading || !selectedEnterprise}
          >
            <option value="">Select Store</option>
            {stores.map((store) => (
              <option key={store.storeId} value={store.storeId}>
                {store.name} - {store.address}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Compliance Dashboard Overview */}
      {selectedStore && audits.length > 0 && (
        <div className="compliance-overview">
          <div className="overview-cards">
            <div className="overview-card">
              <h4>Overall Compliance Score</h4>
              <div className="score-circle">
                <span className={`score-value ${getComplianceColor(audits[0]?.overallScore || 0)}`}>
                  {(audits[0]?.overallScore || 0).toFixed(0)}%
                </span>
              </div>
            </div>
            <div className="overview-card">
              <h4>Active Audits</h4>
              <div className="metric-value">
                {audits.filter(a => a && (a.status === 'IN_PROGRESS' || a.status === 'PENDING')).length}
              </div>
            </div>
            <div className="overview-card">
              <h4>Critical Issues</h4>
              <div className="metric-value critical">
                {Object.values(audits[0]?.requirements || {}).filter(
                  req => req.status === 'NON_COMPLIANT'
                ).length}
              </div>
            </div>
            <div className="overview-card">
              <h4>Compliance Status</h4>
              <div className="status-breakdown">
                <span className="status-item compliant">
                  {Object.values(audits[0]?.requirements || {}).filter(req => req.status === 'COMPLETED').length} Complete
                </span>
                <span className="status-item pending">
                  {Object.values(audits[0]?.requirements || {}).filter(req => req.status === 'IN_PROGRESS').length} In Progress
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="audit-nav">
        <button
          className={activeTab === 'overview' ? 'nav-btn active' : 'nav-btn'}
          onClick={() => setActiveTab('overview')}
        >
          Overview
        </button>
        <button
          className={activeTab === 'audits' ? 'nav-btn active' : 'nav-btn'}
          onClick={() => setActiveTab('audits')}
        >
          Audits
        </button>
        <button
          className={activeTab === 'requirements' ? 'nav-btn active' : 'nav-btn'}
          onClick={() => setActiveTab('requirements')}
        >
          Requirements
        </button>
        <button
          className={activeTab === 'events' ? 'nav-btn active' : 'nav-btn'}
          onClick={() => setActiveTab('events')}
        >
          Audit Events
        </button>
        <button
          className={activeTab === 'checks' ? 'nav-btn active' : 'nav-btn'}
          onClick={() => setActiveTab('checks')}
        >
          Automated Checks
        </button>
        <button
          className={activeTab === 'documents' ? 'nav-btn active' : 'nav-btn'}
          onClick={() => setActiveTab('documents')}
        >
          Documents
        </button>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="overview-section">
          {!selectedStore ? (
            <div className="empty-state">
              <p>Please select a store to view compliance overview</p>
            </div>
          ) : audits.length === 0 ? (
            <div className="empty-state">
              <h3>No Security Audits Found</h3>
              <p>Create your first security audit to begin compliance monitoring</p>
              {canCreateAudits && (
                <div className="quick-actions">
                  <button
                    onClick={() => runAdHocAudit('PCI_LEVEL_1')}
                    className="primary-button"
                    disabled={loading}
                  >
                    {loading ? 'Running...' : 'Run PCI Level 1 Audit Now'}
                  </button>
                  <button
                    onClick={() => runAdHocAudit('SOC2_TYPE_II')}
                    className="primary-button"
                    disabled={loading}
                  >
                    {loading ? 'Running...' : 'Run SOC2 Type II Audit Now'}
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="overview-content">
              <div className="recent-audits">
                <div className="audits-header">
                  <h3>Recent Audits</h3>
                  {canCreateAudits && (
                    <div className="audit-actions">
                      <button
                        onClick={() => runAdHocAudit('PCI_LEVEL_1')}
                        className="secondary-button"
                        disabled={loading}
                        title="Run immediate PCI Level 1 compliance audit"
                      >
                        {loading ? 'Running...' : 'Quick PCI Audit'}
                      </button>
                      <button
                        onClick={() => runAdHocAudit('SOC2_TYPE_II')}
                        className="secondary-button"
                        disabled={loading}
                        title="Run immediate SOC2 Type II compliance audit"
                      >
                        {loading ? 'Running...' : 'Quick SOC2 Audit'}
                      </button>
                    </div>
                  )}
                </div>
                <div className="audit-summary-list">
                  {audits.slice(0, 3).map((audit) => (
                    <div key={audit.auditId} className="audit-summary-card">
                      <div className="audit-summary-header">
                        <h4>{audit.auditType.replace('_', ' ')}</h4>
                        <span className={`status-badge ${getStatusColor(audit.status)}`}>
                          {audit.status.replace('_', ' ')}
                        </span>
                      </div>
                      <div className="audit-summary-details">
                        <div className="score-display">
                          <span className="score-label">Compliance Score:</span>
                          <span className={`score-value ${getComplianceColor(audit.overallScore)}`}>
                            {audit.overallScore.toFixed(1)}%
                          </span>
                        </div>
                        <div className="requirement-status">
                          <span className="compliant">
                            {Object.values(audit.requirements).filter(req => req.status === 'COMPLETED').length} Complete
                          </span>
                          <span className="non-compliant">
                            {Object.values(audit.requirements).filter(req => req.status === 'NON_COMPLIANT').length} Issues
                          </span>
                        </div>
                        <p className="audit-date">Created: {formatDate(audit.createdAt)}</p>
                      </div>
                      <button
                        onClick={() => {
                          setSelectedAudit(audit);
                          setActiveTab('requirements');
                        }}
                        className="secondary-button"
                      >
                        View Details
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="compliance-trends">
                <h3>Compliance Health</h3>
                <div className="health-indicators">
                  <div className="health-item">
                    <div className="health-label">Network Security</div>
                    <div className="health-bar">
                      <div className="health-fill good" style={{width: '85%'}}></div>
                    </div>
                    <span className="health-value">85%</span>
                  </div>
                  <div className="health-item">
                    <div className="health-label">Data Protection</div>
                    <div className="health-bar">
                      <div className="health-fill warning" style={{width: '72%'}}></div>
                    </div>
                    <span className="health-value">72%</span>
                  </div>
                  <div className="health-item">
                    <div className="health-label">Access Control</div>
                    <div className="health-bar">
                      <div className="health-fill good" style={{width: '92%'}}></div>
                    </div>
                    <span className="health-value">92%</span>
                  </div>
                  <div className="health-item">
                    <div className="health-label">Monitoring & Logging</div>
                    <div className="health-bar">
                      <div className="health-fill critical" style={{width: '58%'}}></div>
                    </div>
                    <span className="health-value">58%</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Security Audits Tab */}
      {activeTab === 'audits' && (
        <div className="audits-section">
          <div className="section-header">
            <h3>Security Audits</h3>
            {canCreateAudits && selectedStore && (
              <div className="action-buttons">
                <button
                  onClick={() => createNewAudit('PCI_LEVEL_1')}
                  disabled={loading}
                  className="primary-button"
                >
                  New PCI Level 1 Audit
                </button>
                <button
                  onClick={() => createNewAudit('SOC2_TYPE_II')}
                  disabled={loading}
                  className="primary-button"
                >
                  New SOC2 Type II Audit
                </button>
              </div>
            )}
          </div>

          {loading ? (
            <div className="loading-message">Loading audits...</div>
          ) : !selectedStore ? (
            <div className="empty-state">
              <p>Please select a store to view security audits</p>
            </div>
          ) : audits.length === 0 ? (
            <div className="empty-state">
              <p>No security audits found for this store</p>
              {canCreateAudits && (
                <p>Create your first audit using the buttons above</p>
              )}
            </div>
          ) : (
            <div className="audits-grid">
              {audits.map((audit) => (
                <div key={audit.auditId} className="audit-card">
                  <div className="audit-header">
                    <h4>{audit.auditType.replace('_', ' ')}</h4>
                    <span
                      className={`status-badge ${getStatusColor(audit.status)}`}
                    >
                      {audit.status.replace('_', ' ')}
                    </span>
                  </div>
                  
                  <div className="audit-details">
                    <div className="compliance-score-display">
                      <div className={`score-circle-small ${getComplianceColor(audit.overallScore)}`}>
                        {audit.overallScore.toFixed(0)}%
                      </div>
                      <div className="score-details">
                        <span className="score-label">Compliance Score</span>
                        <div className="requirement-counts">
                          <span className="compliant-count">
                            {Object.values(audit.requirements).filter(req => req.status === 'COMPLETED').length} Complete
                          </span>
                          <span className="non-compliant-count">
                            {Object.values(audit.requirements).filter(req => req.status === 'NON_COMPLIANT').length} Issues
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="audit-metadata">
                      <p><strong>Created:</strong> {formatDate(audit.createdAt)}</p>
                      <p><strong>Expires:</strong> {formatDate(audit.expirationDate)}</p>
                      <p><strong>Created By:</strong> {audit.createdBy}</p>
                    </div>
                  </div>

                  <div className="audit-actions">
                    <button
                      onClick={() => setSelectedAudit(audit)}
                      className="primary-button"
                    >
                      View Details
                    </button>
                    <button
                      onClick={() => window.open(API_ENDPOINTS.securityComplianceReport(audit.auditId), '_blank')}
                      className="secondary-button"
                    >
                      Export Report
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Requirements Tab */}
      {activeTab === 'requirements' && (
        <div className="requirements-section">
          {!selectedAudit ? (
            <div className="empty-state">
              <p>Select an audit from the Audits tab to view detailed requirements</p>
            </div>
          ) : (
            <div className="requirements-content">
              <div className="requirements-header">
                <h3>{selectedAudit.auditType.replace('_', ' ')} Requirements</h3>
                <div className="requirements-summary">
                  <span className="summary-item compliant">
                    {Object.values(selectedAudit.requirements).filter(req => req.status === 'COMPLETED').length} Completed
                  </span>
                  <span className="summary-item in-progress">
                    {Object.values(selectedAudit.requirements).filter(req => req.status === 'IN_PROGRESS').length} In Progress
                  </span>
                  <span className="summary-item non-compliant">
                    {Object.values(selectedAudit.requirements).filter(req => req.status === 'NON_COMPLIANT').length} Non-Compliant
                  </span>
                </div>
              </div>

              <div className="requirements-grid">
                {Object.entries(selectedAudit.requirements).map(([reqId, requirement]) => (
                  <div key={reqId} className={`requirement-card ${requirement.status.toLowerCase()}`}>
                    <div className="requirement-header">
                      <div className="req-id">{reqId}</div>
                      <div className={`req-status ${requirement.status.toLowerCase()}`}>
                        {requirement.status.replace('_', ' ')}
                      </div>
                    </div>

                    <div className="requirement-content">
                      <h4 className="requirement-description">{requirement.description}</h4>
                      {requirement.notes && <p className="requirement-notes"><strong>Notes:</strong> {requirement.notes}</p>}
                      
                      {requirement.evidence.length > 0 && (
                        <div className="evidence-section">
                          <strong>Evidence:</strong>
                          <ul>
                            {requirement.evidence.map((evidence, index) => (
                              <li key={index}>{evidence}</li>
                            ))}
                          </ul>
                        </div>
                      )}

                      <div className="documents-section" style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #dee2e6' }}>
                        <strong style={{ display: 'block', marginBottom: '12px' }}>Required Documents:</strong>
                        {requirement.requiredDocuments && requirement.requiredDocuments.length > 0 ? (
                          <div className="required-documents-list" style={{ display: 'grid', gap: '10px' }}>
                            {requirement.requiredDocuments.map((docName, index) => {
                              const uploaded = requirement.uploadedDocuments?.[docName];
                              return (
                                <div key={index} style={{
                                  background: uploaded ? '#d4edda' : '#fff3cd',
                                  border: `1px solid ${uploaded ? '#c3e6cb' : '#ffc107'}`,
                                  borderRadius: '6px',
                                  padding: '10px',
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center'
                                }}>
                                  <div style={{ flex: 1 }}>
                                    <div style={{ fontWeight: '500', fontSize: '0.95rem', marginBottom: '4px' }}>
                                      {docName}
                                      {AUTO_GENERATABLE_DOCUMENTS.includes(docName) && (
                                        <span style={{
                                          marginLeft: '8px',
                                          fontSize: '0.7rem',
                                          background: '#17a2b8',
                                          color: 'white',
                                          padding: '2px 6px',
                                          borderRadius: '3px',
                                          fontWeight: 'normal'
                                        }}>
                                          Auto-Gen Available
                                        </span>
                                      )}
                                    </div>
                                    {uploaded ? (
                                      <div style={{ fontSize: '0.8rem', color: '#155724' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                                          <span>✓ {uploaded.fileName} ({formatFileSize(uploaded.fileSize)})</span>
                                          {uploaded.autoGenerated && uploaded.pdfContent && (
                                            <button
                                              onClick={() => downloadGeneratedDocument(uploaded, docName)}
                                              style={{
                                                cursor: 'pointer',
                                                background: '#28a745',
                                                color: 'white',
                                                padding: '2px 8px',
                                                borderRadius: '3px',
                                                fontSize: '0.7rem',
                                                fontWeight: '500',
                                                border: 'none',
                                                whiteSpace: 'nowrap'
                                              }}
                                            >
                                              Download PDF
                                            </button>
                                          )}
                                        </div>
                                        <span style={{ fontSize: '0.75rem' }}>
                                          {uploaded.autoGenerated ? 'Auto-generated' : 'Uploaded'} {formatDate(uploaded.uploadedAt)} by {uploaded.uploadedBy}
                                        </span>
                                      </div>
                                    ) : (
                                      <div style={{ fontSize: '0.8rem', color: '#856404' }}>
                                        📋 Document required - not yet uploaded
                                      </div>
                                    )}
                                  </div>
                                  <div style={{
                                    display: 'flex',
                                    gap: '8px',
                                    marginLeft: '12px',
                                    flexShrink: 0,
                                    alignItems: 'center'
                                  }}>
                                    {AUTO_GENERATABLE_DOCUMENTS.includes(docName) && (
                                      <button
                                        onClick={() => autoGenerateDocument(reqId, docName)}
                                        disabled={generatingDocument === docName}
                                        style={{
                                          cursor: generatingDocument === docName ? 'not-allowed' : 'pointer',
                                          background: generatingDocument === docName ? '#6c757d' : '#17a2b8',
                                          color: 'white',
                                          padding: '6px 12px',
                                          borderRadius: '4px',
                                          fontSize: '0.8rem',
                                          fontWeight: '500',
                                          border: 'none',
                                          whiteSpace: 'nowrap',
                                          opacity: generatingDocument === docName ? 0.6 : 1,
                                          minWidth: '110px'
                                        }}
                                      >
                                        {generatingDocument === docName ? 'Generating...' : 'Auto-Generate'}
                                      </button>
                                    )}
                                    <label style={{
                                      cursor: 'pointer',
                                      background: uploaded ? '#28a745' : '#007bff',
                                      color: 'white',
                                      padding: '6px 12px',
                                      borderRadius: '4px',
                                      fontSize: '0.8rem',
                                      fontWeight: '500',
                                      border: 'none',
                                      whiteSpace: 'nowrap',
                                      display: 'inline-block',
                                      minWidth: '70px',
                                      textAlign: 'center'
                                    }}>
                                      {uploaded ? 'Replace' : 'Upload'}
                                      <input
                                        type="file"
                                        style={{ display: 'none' }}
                                        onChange={(e) => handleFileSelect(e, reqId, docName)}
                                        accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
                                      />
                                    </label>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <p style={{ fontSize: '0.85rem', color: '#666', fontStyle: 'italic' }}>
                            No specific documents required for this requirement
                          </p>
                        )}
                      </div>

                      {requirement.remediation && (
                        <div className="remediation-section">
                          <strong>Required Action:</strong>
                          <p>{requirement.remediation}</p>
                        </div>
                      )}
                    </div>
                    
                    <div className="requirement-footer">
                      {requirement.lastChecked && (
                        <span>Last Checked: {formatDate(requirement.lastChecked)}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Audit Events Tab */}
      {activeTab === 'events' && (
        <div className="events-section">
          <div className="section-header">
            <h3>Audit Event Log</h3>
            <p>Real-time security and compliance event monitoring</p>
          </div>
          
          {!selectedStore ? (
            <div className="empty-state">
              <p>Please select a store to view audit events</p>
            </div>
          ) : (
            <div className="events-content">
              <div className="events-placeholder">
                <h4>Audit Event Monitoring</h4>
                <p>This section will display real-time security events including:</p>
                <ul>
                  <li>Access control changes</li>
                  <li>Configuration modifications</li>
                  <li>Security policy violations</li>
                  <li>Compliance check results</li>
                  <li>User authentication events</li>
                </ul>
                <div className="coming-soon">
                  <p><em>Event monitoring integration coming soon...</em></p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Automated Checks Tab */}
      {activeTab === 'checks' && (
        <div className="checks-section">
          <div className="section-header">
            <h3>Automated Compliance Checks</h3>
            {canRunChecks && selectedStore && (
              <button
                onClick={runAutomatedChecks}
                disabled={runningChecks}
                className="primary-button"
              >
                {runningChecks ? 'Running Checks...' : 'Run Automated Checks'}
              </button>
            )}
          </div>

          {!selectedStore ? (
            <div className="empty-state">
              <p>Please select a store to run automated compliance checks</p>
            </div>
          ) : (
            <div className="checks-info">
              <h4>Automated Compliance Checks Include:</h4>
              <ul>
                <li><strong>Password Policy:</strong> Validates IAM password requirements</li>
                <li><strong>Key Rotation:</strong> Checks KMS key rotation status</li>
                <li><strong>Backup Status:</strong> Verifies database backup configuration</li>
                <li><strong>Encryption Status:</strong> Validates encryption at rest and in transit</li>
                <li><strong>Network Security:</strong> Reviews security groups and network ACLs</li>
              </ul>
              
              {complianceChecks.length > 0 && (
                <div className="recent-checks">
                  <h4>Recent Check Results:</h4>
                  {complianceChecks.slice(0, 5).map((check) => (
                    <div key={check.checkId} className="check-result">
                      <div className="check-header">
                        <span>{check.checkType}</span>
                        <span className={`compliance-score ${getComplianceColor(check.overallCompliance)}`}>
                          {(check.overallCompliance * 100).toFixed(1)}% Compliant
                        </span>
                      </div>
                      <p className="check-date">Last checked: {formatDate(check.lastChecked)}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Document Management Tab */}
      {activeTab === 'documents' && (
        <div className="documents-section">
          <div className="section-header">
            <h3>Compliance Documents</h3>
            <p style={{ fontSize: '0.9rem', color: '#666', marginTop: '8px' }}>
              All uploaded supporting documents for audit requirements
            </p>
          </div>

          {!selectedAudit ? (
            <div className="empty-state">
              <p>Select an audit from the Audits tab to view documents</p>
            </div>
          ) : allDocuments.length === 0 ? (
            <div className="empty-state">
              <h4>No Documents Uploaded</h4>
              <p>Upload supporting documents for each requirement in the Requirements tab</p>
            </div>
          ) : (
            <div className="documents-content" style={{ padding: '20px' }}>
              <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h4>All Documents ({allDocuments.length})</h4>
                <span style={{ color: '#666', fontSize: '0.9rem' }}>
                  Audit: {selectedAudit.auditType.replace('_', ' ')}
                </span>
              </div>

              <div className="documents-list" style={{ display: 'grid', gap: '12px' }}>
                {allDocuments.map((doc) => {
                  const requirement = selectedAudit.requirements[doc.requirementId || ''];
                  return (
                    <div key={doc.documentId} style={{
                      background: 'white',
                      border: '1px solid #dee2e6',
                      borderRadius: '8px',
                      padding: '16px',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '8px' }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: '600', fontSize: '1.05rem', marginBottom: '4px' }}>
                            {doc.fileName}
                          </div>
                          {doc.requirementId && (
                            <div style={{ fontSize: '0.85rem', color: '#666', marginBottom: '4px' }}>
                              Requirement: {doc.requirementId}
                              {requirement && ` - ${requirement.description.substring(0, 80)}...`}
                            </div>
                          )}
                        </div>
                        <div style={{
                          background: '#28a745',
                          color: 'white',
                          padding: '4px 12px',
                          borderRadius: '12px',
                          fontSize: '0.8rem',
                          fontWeight: '500'
                        }}>
                          {formatFileSize(doc.fileSize)}
                        </div>
                      </div>

                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                        gap: '12px',
                        paddingTop: '12px',
                        borderTop: '1px solid #e9ecef',
                        fontSize: '0.9rem',
                        color: '#666'
                      }}>
                        <div>
                          <strong>Last Updated:</strong>
                          <div style={{ marginTop: '4px', color: '#28a745', fontWeight: '500' }}>
                            {formatDate(doc.uploadedAt)}
                          </div>
                        </div>
                        <div>
                          <strong>Uploaded By:</strong>
                          <div style={{ marginTop: '4px' }}>
                            {doc.uploadedBy}
                          </div>
                        </div>
                        <div>
                          <strong>Document Type:</strong>
                          <div style={{ marginTop: '4px' }}>
                            {doc.documentType.split('/')[1]?.toUpperCase() || 'FILE'}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SecurityAuditManagement;